"use client";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useRouter } from "next/navigation";
import { TooltipContent } from "./tooltip-content";

interface ModalProps {
	title?: string;
	logo?: React.ReactNode;
	description?: string;
	className?: string;
	trigger?: React.ReactNode;
	children?: React.ReactNode;
	opened?: boolean;
	closeable?: boolean;
	onClose?: () => void;
}

export default function Modal(props: ModalProps) {
	const router = useRouter();

	const {
		title,
		logo = false,
		description,
		className,
		trigger,
		children,
		opened = false,
		closeable = true,
		onClose = () => {
			router.back();
		},
	} = props;

	const handleDialogClose = (open: boolean) => {
		if (!open && onClose) {
			onClose();
		}
	};

	return (
		<Dialog open={opened} onOpenChange={handleDialogClose}>
			<TooltipContent content={title}>
				<DialogTrigger asChild>{trigger}</DialogTrigger>
			</TooltipContent>
			<DialogContent
				className={cn(
					"z-50 flex flex-col p-1 items-center justify-center",
					"w-full rounded-sm md:rounded-2xl bg-background backdrop-blur-2xl border border-primary-34",
					"duration-300 ease-out data-closed:transform-[scale(95%)] data-closed:opacity-0",
					"[&>button:last-child]:hidden", // Hide close button
					className,
				)}
			>
				<DialogHeader className="z-50">
					{logo}
					<DialogTitle className="text-3xl text-center font-bold text-shadow-md drop-shadow-lg">
						{title}
					</DialogTitle>
					{description ? (
						<DialogDescription className="text-sm/6 text-primary/50 text-center">
							{description}
						</DialogDescription>
					) : (
						<DialogDescription className="sr-only">{title}</DialogDescription>
					)}
				</DialogHeader>

				{children}

				{closeable && (
					<DialogClose asChild>
						<Button
							type="button"
							variant="ghost"
							className="text-primary absolute top-2 right-2 p-2 rounded-full"
						>
							<X className="h-4 w-4" />
							<span className="sr-only">Close</span>
						</Button>
					</DialogClose>
				)}
			</DialogContent>
		</Dialog>
	);
}
